import React from 'react';
import { Backup } from '../../types/api';
import BackupStatusBadge from './BackupStatusBadge';
import { useTranslation } from 'react-i18next';

interface BackupCardProps {
  backup: Backup;
  onRestore: (backupId: string) => void;
  onDelete?: (backupId: string) => void;
  disabled?: boolean;
}

const BackupCard: React.FC<BackupCardProps> = ({ backup, onRestore, onDelete, disabled = false }) => {
  const { t } = (useTranslation as any)('backups');
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatSize = (bytes: number | null) => {
    if (bytes === null) return 'N/A';
    const mb = bytes / (1024 * 1024);
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb >= 1) {
      return `${gb.toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  const isRestoreDisabled = disabled || backup.status !== 'completed';

  return (
    <div className={`bg-surface-2 rounded-lg border border-border-1 p-4 hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className={`text-sm font-semibold text-text-primary`}>
              {backup.backup_name}
            </h3>
            <BackupStatusBadge status={backup.status} />
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              backup.backup_type === 'manual'
                ? 'bg-blue-500/12 text-blue-400'
                : 'bg-surface-3 text-text-secondary'
            }`}>
              {backup.backup_type === 'manual' ? t('backupCard.manual') as string : t('backupCard.automatic') as string}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <span className={`text-text-secondary`}>Créé le:</span>
              <p className={`text-text-primary mt-0.5`}>{formatDate(backup.started_at)}</p>
            </div>
            {backup.completed_at && (
              <div>
                <span className={`text-text-secondary`}>Terminé le:</span>
                <p className={`text-text-primary mt-0.5`}>{formatDate(backup.completed_at)}</p>
              </div>
            )}
            <div>
              <span className={`text-text-secondary`}>Taille:</span>
              <p className={`text-text-primary mt-0.5 font-mono`}>{formatSize(backup.size_bytes)}</p>
            </div>
            <div>
              <span className={`text-text-secondary`}>ID:</span>
              <p className={`text-text-primary mt-0.5 font-mono text-xs truncate`}>{backup.id.substring(0, 8)}...</p>
            </div>
          </div>

          {backup.error_message && (
            <div className="mt-3 p-2 bg-red-500/12 rounded text-xs text-red-400">
              <span className="font-semibold">Erreur:</span> {backup.error_message}
            </div>
          )}
        </div>

        <div className="ml-4 flex flex-col gap-2">
          <button
            onClick={() => onRestore(backup.id)}
            disabled={isRestoreDisabled}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              isRestoreDisabled
                ? 'bg-surface-3 text-text-quaternary cursor-not-allowed'
                : 'bg-accent-600 hover:bg-accent-700 text-white'
            }`}
            title={isRestoreDisabled ? 'Seuls les backups terminés peuvent être restaurés' : 'Restaurer ce backup'}
          >
            {backup.status === 'completed' ? t('backupCard.restore') as string : t('backupCard.unavailable') as string}
          </button>
          {onDelete && (
            <button
              onClick={() => onDelete(backup.id)}
              disabled={isRestoreDisabled}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                isRestoreDisabled
                  ? 'bg-surface-3 text-text-quaternary cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
              title={isRestoreDisabled ? 'Seuls les backups terminés peuvent être supprimés' : 'Supprimer ce backup'}
            >
              Supprimer
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackupCard;
